/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * ApplicationUserDetailsModal
*
* Props:
*  - open: boolean
*  - onClose: () => void
*  - loading: boolean
 *  - error: string
 *  - details: {
 *      id,
 *      organization,
 *      applications: number[],
 *      username,
 *      email,
 *      first_name,
 *      last_name,
 *      role: { id, name, data_scope } | null,
 *      table_permissions: [
 *        {
 *          id,
 *          table_id,
 *          table_name,
 *          table_display_name,
 *          permissions: string[],
 *          default_inherit: boolean,
 *          data_scope: string,
 *        }
 *      ]
 *    } | null
*/
import React from "react";
import { useTheme } from "../../theme/ThemeContext";
import Button from "../Button";

export default function ApplicationUserDetailsModal({
    open,
    onClose,
    loading,
    error,
    details,
    applicationId,
}) {
    const { theme } = useTheme();
    const [assignments, setAssignments] = React.useState([]);
    const [assignmentsLoading, setAssignmentsLoading] = React.useState(false);

    React.useEffect(() => {
        if (open && details && details.id && applicationId) {
            fetchAssignments();
        } else {
            setAssignments([]);
        }
    }, [open, details, applicationId]);

    const fetchAssignments = async () => {
        setAssignmentsLoading(true);
        try {
            const apiHelper = new (require('../../api/APIHelper').default)({
                apiUrl: require('../../config/envConfig').default.backendApiUrl,
                tokenUrl: require('../../config/envConfig').default.backendTokenUrl,
                clientId: require('../../config/envConfig').default.clientKey,
                clientSecret: require('../../config/envConfig').default.clientSecret
            });

            const resp = await apiHelper.apiCall(`/arcee/group/assign/list/?user_id=${details.id}&application_id=${applicationId}`);
            if (resp.ok) {
                const data = await resp.json();
                setAssignments(data.assignments || data.results || (Array.isArray(data) ? data : []));
            }
        } catch (e) {
            console.error("Failed to load assignments", e);
        } finally {
            setAssignmentsLoading(false);
        }
    };

    if (!open) return null;

    const backdropStyle = {
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(15,23,42,0.55)",
        zIndex: 1040,
    };

    const cardStyle = {
        maxWidth: "900px",
        width: "96vw",
        background: "#ffffff",
        borderRadius: theme.cardRadius,
        boxShadow: "0 20px 60px rgba(15,23,42,0.35)",
        margin: "40px auto",
        position: "relative",
    };

    const headerStyle = {
        padding: "1rem 1.5rem",
        borderBottom: "1px solid #e2e8f0",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        color: theme.primary,
    };

    const bodyStyle = {
        padding: "1.25rem 1.5rem 1.5rem",
        maxHeight: "70vh",
        overflowY: "auto",
    };

    const footerStyle = {
        padding: "0.9rem 1.5rem 1.1rem",
        borderTop: "1px solid #e2e8f0",
        display: "flex",
        justifyContent: "flex-end",
    };

    const chipStyle = {
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: "999px",
        fontSize: "0.75rem",
        background: "#f1f5f9",
        color: "#0f172a",
        marginRight: 6,
        marginBottom: 4,
    };

    const tableHeadCell = {
        fontSize: "0.8rem",
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        color: "#64748b",
        borderBottom: "1px solid #e2e8f0",
        padding: "0.6rem 0.5rem",
    };

    const tableCell = {
        fontSize: "0.85rem",
        padding: "0.5rem 0.5rem",
        borderBottom: "1px solid #f1f5f9",
        verticalAlign: "top",
    };

    const tableWrapperStyle = {
        borderRadius: 10,
        border: "1px solid #e2e8f0",
        background: "#f8fafc",
        padding: "0.75rem",
        maxHeight: "260px",
        overflowY: "auto",
    };

    const permissionsChip = {
        display: "inline-block",
        padding: "2px 7px",
        borderRadius: 999,
        fontSize: "0.75rem",
        background: "#e0f2fe",
        color: "#0f172a",
        marginRight: 4,
        marginBottom: 4,
    };

    const scopeBadge = {
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 999,
        fontSize: "0.75rem",
        background: "#eef2ff",
        color: "#3730a3",
    };

    const inheritBadge = (isDefault) => ({
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 8px",
        borderRadius: 999,
        fontSize: "0.75rem",
        background: isDefault ? "#dcfce7" : "#fee2e2",
        color: isDefault ? "#166534" : "#b91c1c",
    });

    const tableRowStyle = {
        transition: "background-color 120ms ease",
    };


    const stopPropagation = (e) => e.stopPropagation();

    return (
        <>
            <div style={backdropStyle} onClick={onClose} />
            <div
                className="d-flex align-items-start justify-content-center"
                style={{ position: "fixed", inset: 0, zIndex: 1050, overflowY: "auto" }}
                onClick={onClose}
            >
                <div style={cardStyle} onClick={stopPropagation}>
                    {/* Header */}
                    <div style={headerStyle}>
                        <div>
                            <h5 className="mb-0 fw-bold">User Details</h5>
                            {details && (
                                <div style={{ fontSize: "0.82rem", color: "#64748b" }}>
                                    #{details.id} · {details.username}
                                </div>
                            )}
                        </div>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={onClose}
                        >
                            <i className="bi bi-x-lg"></i>
                        </button>
                    </div>

                    {/* Body */}
                    <div style={bodyStyle}>
                        {loading && (
                            <div className="text-center py-4">
                                <div className="spinner-border spinner-border-sm mb-2" />
                                <div style={{ fontSize: "0.9rem", color: "#64748b" }}>
                                    Loading user details...
                                </div>
                            </div>
                        )}

                        {!loading && error && (
                            <div className="alert alert-danger" role="alert">
                                {error}
                            </div>
                        )}

                        {!loading && !error && details && (
                            <>
                                {/* Profile + Role */}
                                <div className="row mb-3">
                                    <div className="col-md-6 mb-3">
                                        <h6 className="fw-semibold mb-2" style={{ color: theme.primary }}>
                                            Profile
                                        </h6>
                                        <div style={{ fontSize: "0.9rem" }}>
                                            <div>
                                                <span className="fw-semibold">Username: </span>
                                                {details.username || "-"}
                                            </div>
                                            <div>
                                                <span className="fw-semibold">Email: </span>
                                                {details.email || "-"}
                                            </div>
                                            <div>
                                                <span className="fw-semibold">First Name: </span>
                                                {details.first_name || "-"}
                                            </div>
                                            <div>
                                                <span className="fw-semibold">Last Name: </span>
                                                {details.last_name || "-"}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-md-6 mb-3">
                                        <h6 className="fw-semibold mb-2" style={{ color: theme.primary }}>
                                            Role
                                        </h6>
                                        {details.role ? (
                                            <div style={{ fontSize: "0.9rem" }}>
                                                <div>
                                                    <span className="fw-semibold">Role Name: </span>
                                                    {details.role.name}
                                                </div>
                                                <div>
                                                    <span className="fw-semibold">Data Scope: </span>
                                                    {details.role.data_scope}
                                                </div>
                                            </div>
                                        ) : (
                                            <div style={{ fontSize: "0.9rem", color: "#94a3b8" }}>
                                                No role assigned for this application.
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Applications */}
                                <div className="mb-3">
                                    <h6 className="fw-semibold mb-2" style={{ color: theme.primary }}>
                                        Applications
                                    </h6>

                                    {details.applications && details.applications.length > 0 ? (
                                        <div>
                                            {details.applications.map((app, idx) => (
                                                <span
                                                    key={app.id || idx}
                                                    style={{
                                                        display: "inline-block",
                                                        padding: "3px 10px",
                                                        background: "#e0f2fe",
                                                        borderRadius: 999,
                                                        marginRight: 6,
                                                        marginBottom: 6,
                                                        fontSize: "0.8rem",
                                                        color: "#0f172a",
                                                    }}
                                                >
                                                    {app.name}
                                                </span>
                                            ))}
                                        </div>
                                    ) : (
                                        <div style={{ fontSize: "0.9rem", color: "#94a3b8" }}>
                                            No applications assigned.
                                        </div>
                                    )}
                                </div>

                                {/* Group Assignments */}
                                <div className="mt-3">
                                    <h6 className="fw-semibold mb-2" style={{ color: theme.primary }}>
                                        Group Assignments
                                    </h6>
                                    {assignmentsLoading && <div className="spinner-border spinner-border-sm text-primary mb-2"></div>}
                                    {!assignmentsLoading && assignments.length > 0 ? (
                                        <div style={tableWrapperStyle}>
                                            <div className="table-responsive">
                                                <table className="table mb-0" style={{ fontSize: "0.85rem" }}>
                                                    <thead>
                                                        <tr>
                                                            <th style={tableHeadCell}>Group</th>
                                                            <th style={tableHeadCell}>Type</th>
                                                            <th style={tableHeadCell}>Scope</th>
                                                            <th style={tableHeadCell}>Table Access</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {assignments.map((assign) => (
                                                            <tr
                                                                key={assign.id}
                                                                style={tableRowStyle}
                                                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f1f5f9")}
                                                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                                                            >
                                                                <td style={tableCell}>
                                                                    <div className="fw-semibold">{assign.group_name || '-'}</div>
                                                                </td>
                                                                <td style={tableCell}>
                                                                    <span className="text-muted">{assign.group_type || '-'}</span>
                                                                </td>
                                                                <td style={tableCell}>
                                                                    <span style={scopeBadge}>{assign.rls_scope}</span>
                                                                </td>
                                                                <td style={tableCell}>
                                                                    {assign.applies_to_all_tables ? (
                                                                        <span className="text-success fw-semibold">
                                                                            <i className="bi bi-check-circle-fill me-1"></i>All Tables
                                                                            {assign.discarded_tables?.length > 0 && <span className="text-danger ms-1">({assign.discarded_tables.length} exceptions)</span>}
                                                                        </span>
                                                                    ) : (
                                                                        <span className="text-muted">
                                                                            Specific ({assign.allowed_tables?.length || 0})
                                                                        </span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    ) : (
                                        !assignmentsLoading && (
                                            <div style={{ fontSize: "0.9rem", color: "#94a3b8", marginBottom: "1rem" }}>
                                                No group assignments found for this user.
                                            </div>
                                        )
                                    )}
                                </div>

                                {/* Table Permissions */}
                                <div className="mt-3">
                                    <h6 className="fw-semibold mb-2" style={{ color: theme.primary }}>
                                        Table Permissions
                                    </h6>

                                    {details.table_permissions && details.table_permissions.length > 0 ? (
                                        <div style={tableWrapperStyle}>
                                            <div className="table-responsive">
                                                <table className="table mb-0" style={{ fontSize: "0.85rem" }}>
                                                    <thead>
                                                        <tr>
                                                            <th style={tableHeadCell}>Table</th>
                                                            <th style={tableHeadCell}>Permissions</th>
                                                            <th style={tableHeadCell}>Data Scope</th>
                                                            <th style={tableHeadCell}>Default Inherit</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {details.table_permissions.map((perm) => (
                                                            <tr
                                                                key={perm.id}
                                                                style={tableRowStyle}
                                                                onMouseEnter={(e) =>
                                                                    (e.currentTarget.style.backgroundColor = "#f1f5f9")
                                                                }
                                                                onMouseLeave={(e) =>
                                                                    (e.currentTarget.style.backgroundColor = "transparent")
                                                                }
                                                            >
                                                                <td style={tableCell}>
                                                                    <div className="fw-semibold">
                                                                        {perm.table_display_name || perm.table_name}
                                                                    </div>
                                                                    <div
                                                                        style={{
                                                                            fontSize: "0.78rem",
                                                                            color: "#94a3b8",
                                                                        }}
                                                                    >
                                                                        ({perm.table_name})
                                                                    </div>
                                                                </td>

                                                                <td style={tableCell}>
                                                                    {Array.isArray(perm.permissions) && perm.permissions.length > 0 ? (
                                                                        perm.permissions.map((p) => (
                                                                            <span key={p} style={permissionsChip}>
                                                                                {p}
                                                                            </span>
                                                                        ))
                                                                    ) : (
                                                                        <span style={{ color: "#94a3b8" }}>No explicit permissions</span>
                                                                    )}
                                                                </td>

                                                                <td style={tableCell}>
                                                                    {perm.data_scope ? (
                                                                        <span style={scopeBadge}>{perm.data_scope}</span>
                                                                    ) : (
                                                                        <span style={{ color: "#94a3b8" }}>–</span>
                                                                    )}
                                                                </td>

                                                                <td style={tableCell}>
                                                                    <span style={inheritBadge(perm.default_inherit)}>
                                                                        <span
                                                                            style={{
                                                                                width: 6,
                                                                                height: 6,
                                                                                borderRadius: "50%",
                                                                                marginRight: 6,
                                                                                backgroundColor: perm.default_inherit ? "#16a34a" : "#ef4444",
                                                                            }}
                                                                        />
                                                                        {perm.default_inherit ? "Inherit by default" : "Override only"}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    ) : (
                                        <div style={{ fontSize: "0.9rem", color: "#94a3b8" }}>
                                            No table permissions configured for this user.
                                        </div>
                                    )}
                                </div>
                            </>
                        )}

                    </div>

                    {/* Footer */}
                    <div style={footerStyle}>
                        <Button
                            mode="primary-bold"
                            onClick={onClose}
                        >
                            Close
                        </Button>
                    </div>
                </div>
            </div>
        </>
    );
}
